import { ChatConversationMessage, GenericChatMessage, Meeting } from '../models/chat';
import { AbstractStore, ActionHandlers, BaseStoreAction } from './abstract.store';
import { CacheService } from './cache.service';
import { PageVisibilityService } from './page-visibility.service';
import { AuthServiceInterface } from './AuthServiceInterface';

export type MeetingsStoreState = {
    isMeetingListOpen: boolean;
    isLoaded: boolean;
    isStuckToBottom: boolean;
    activeMeeting: Meeting | null;
    meetings: Meeting[];
};

const isMobile = window.innerWidth < 768;

export class MeetingsStore extends AbstractStore<BaseStoreAction, MeetingsStoreState> {
    static $inject = ['AuthService', 'CacheService', 'PageVisibilityService'];

    constructor(
        private authService: AuthServiceInterface,
        private cacheService: CacheService,
        private pageVisibilityService: PageVisibilityService
    ) {
        super();

        const cacheKeyIsMeetingListOpen = 'MeetingsStore__isMeetingListOpen';
        this.dispatch({
            type: 'set_meeting_list_open',
            // hide meeting list by default on mobile
            payload: isMobile ? false : this.cacheService.fromCache(cacheKeyIsMeetingListOpen, true),
        });
        this.isMeetingListOpen$.subscribe((v) => this.cacheService.toCache(cacheKeyIsMeetingListOpen, v));
    }

    get meetings$() {
        return this.store$.filter((s) => s.isLoaded).map((s) => s.meetings);
    }

    get activeMeeting$() {
        return this.store$.map((s) => s.activeMeeting).filter((v) => Boolean(v));
    }

    get isMeetingListOpen$() {
        return this.store$.map((s) => s.isMeetingListOpen);
    }

    get isStuckToBottom$() {
        return this.store$.map((store) => store.isStuckToBottom).distinctUntilChanged();
    }

    createInitialState(): MeetingsStoreState {
        return {
            isMeetingListOpen: true,
            isLoaded: false,
            isStuckToBottom: true,
            activeMeeting: null,
            meetings: [],
        };
    }

    getActionHandlers(): ActionHandlers<BaseStoreAction, MeetingsStoreState> {
        return {
            set_meeting_list_open: (state, isMeetingListOpen) => ({
                ...state,
                isMeetingListOpen,
            }),
            toggle_meeting_list: (state, payload) => ({
                ...state,
                isMeetingListOpen: !state.isMeetingListOpen,
            }),
            meetings: (state, payload: Meeting[]) => ({
                ...state,
                isLoaded: true,
                meetings: [
                    this.createPublicMeeting(), // append public meeting to the top of the list
                    ...payload.map((meeting: Meeting) => ({
                        ...meeting,
                        lastMessageDate: meeting.last_message ? new Date(meeting.last_message.created_at) : null,
                        newMessageCount: meeting.unread_count || 0,
                        missedCallsCount: meeting.missed_calls_count || 0,
                    })),
                ],
            }),
            active: (state, payload: Meeting) => ({
                ...state,
                activeMeeting: payload,
                meetings: state.meetings.map((meeting) => {
                    if (meeting.id !== payload.id) {
                        return meeting;
                    }

                    return {
                        ...meeting,
                        last_seen_at: new Date(),
                        newMessageCount: 0,
                    };
                }),
            }),
            message_updated: (state, payload: { message: GenericChatMessage; meetingId: number }) => ({
                ...state,
                meetings: state.meetings.map((meeting) => {
                    const { meetingId, message } = payload;

                    if (meetingId !== meeting.id) {
                        return meeting;
                    }

                    // we only use this for updating missed call count
                    if (message.type !== 'call_event') {
                        return meeting;
                    }

                    const thisMeetingIsActive = state.activeMeeting?.id === meeting.id;

                    const newMeetingState = {
                        ...meeting,
                    };

                    if (thisMeetingIsActive && state.isStuckToBottom) {
                        newMeetingState.missedCallsCount = 0;
                    } else {
                        // check if the event is actually a missed call
                        // it can also be a 'started' or 'answered' call
                        if (['unanswered', 'rejected', 'canceled'].includes(message.content.event_type)) {
                            newMeetingState.missedCallsCount += 1;
                        }
                    }

                    return newMeetingState;
                }),
            }),
            new_message: (state, payload: { message: GenericChatMessage; meetingId: number }) => ({
                ...state,
                meetings: state.meetings.map((meeting) => {
                    const { meetingId, message } = payload;

                    if (meetingId !== meeting.id) {
                        return meeting;
                    }

                    return {
                        ...meeting,
                        last_message: message,
                        lastMessageDate: new Date(message.created_at),
                    };
                }),
            }),
            stuck_to_bottom: (state, isStuckToBottom) => ({
                ...state,
                isStuckToBottom,
            }),
            set_unread_count: (state, { meetingId, count }) => ({
                ...state,
                meetings: state.meetings.map((meeting) => {
                    if (meeting.id == meetingId) meeting.newMessageCount = count;
                    return meeting;
                }),
            }),
            increment_unread_count: (state, { meetingId }) => ({
                ...state,
                meetings: state.meetings.map((meeting) => {
                    if (meeting.id == meetingId) {
                        meeting.newMessageCount++;
                    }
                    return meeting;
                }),
            }),
            set_missed_calls_count: (state, { meetingId, count }) => ({
                ...state,
                meetings: state.meetings.map((meeting) => {
                    if (meeting.id == meetingId) meeting.missedCallsCount = count;
                    return meeting;
                }),
            }),
            increment_missed_calls_count: (state, { meetingId }) => ({
                ...state,
                meetings: state.meetings.map((meeting) => {
                    if (meeting.id == meetingId) {
                        meeting.missedCallsCount++;
                    }
                    return meeting;
                }),
            }),
        };
    }

    dispatchToggleMeetingList() {
        this.dispatch({ type: 'toggle_meeting_list', payload: {} });
    }

    dispatchSetMeetings(meetings: Meeting[]) {
        this.dispatch({ type: 'meetings', payload: meetings });
    }

    dispatchSetActiveMeeting(meeting: Meeting) {
        this.dispatch({ type: 'active', payload: meeting });
    }

    dispatchNewMessage(meetingId: number, message: ChatConversationMessage) {
        this.dispatch({ type: 'new_message', payload: { meetingId, message } });
    }

    dispatchMessageUpdated(meetingId: number, message: ChatConversationMessage) {
        this.dispatch({ type: 'message_updated', payload: { meetingId, message } });
    }

    dispatchIsStuckToBottom(following: boolean) {
        this.dispatch({ type: 'stuck_to_bottom', payload: following });
    }

    dispatchSetUnreadCount(meetingId: number, count: number) {
        this.dispatch({ type: 'set_unread_count', payload: { meetingId, count } });
    }

    dispatchSetMissedCallsCount(meetingId: number, count: number) {
        this.dispatch({ type: 'set_missed_calls_count', payload: { meetingId, count } });
    }

    dispatchIncrementUnreadCount(meetingId: number) {
        this.dispatch({ type: 'increment_unread_count', payload: { meetingId } });
    }

    dispatchIncrementMissedCallsCount(meetingId: number) {
        this.dispatch({ type: 'increment_missed_calls_count', payload: { meetingId } });
    }

    private createPublicMeeting(): Meeting {
        return {
            id: 0,
            type: 'community',
            entry: '',
            name: 'Public Chat',
            newMessageCount: 0,
            participants: {}, // we better not list all participants anyway :)
            thumbnail_url: '', // this is fine - we had no thumbnail before either
            unread_count: 0,
            missed_calls_count: 0,
            missedCallsCount: 0,
            video_conference_url: '',
        };
    }
}

angular.module('tradersyard').service('MeetingsStore', MeetingsStore);
